import { useEffect, useState, useRef } from 'react';
import { useFetchRefreshSignedCookie } from '@api';
import { makeSafeUrl } from '@utils';

interface ISecureImageProps {
  imageUrl: string;
  imageStyle: React.CSSProperties | undefined;
}

// 전역 쿠키 갱신 Promise를 관리
let globalRefreshPromise: Promise<any> | null = null;

export const SecureImage = (props: ISecureImageProps) => {
  const { imageUrl, imageStyle } = props;
  const mountedRef = useRef(false);
  const [imageSrc, setImageSrc] = useState<string>('');
  const haveTriedRef = useRef<boolean>(false);
  const { refetch } = useFetchRefreshSignedCookie();

  const refreshCookie = async () => {
    if (!globalRefreshPromise) {
      globalRefreshPromise = refetch().then((result) => {
        globalRefreshPromise = null;
        return result;
      });
    }
    return globalRefreshPromise;
  };

  const tryLoadImage = async (url: string) => {
    if (!url) return;

    try {
      const safeUrl = makeSafeUrl(url);

      const preloadImage = new Image();
      preloadImage.src = safeUrl;

      await new Promise<void>((resolve, reject) => {
        preloadImage.onload = () => resolve();
        preloadImage.onerror = () => reject(new Error('Image preload failed'));
      });

      if (mountedRef.current) {
        setImageSrc(safeUrl);
      }
    } catch (error) {
      console.warn('이미지 로드 실패, 쿠키 갱신 시도', error);

      if (!haveTriedRef.current) {
        haveTriedRef.current = true;
        try {
          const refreshed = await refreshCookie();
          if (refreshed?.data && mountedRef.current) {
            await new Promise((r) => setTimeout(r, 100));
            await tryLoadImage(url);
          }
        } catch (refreshError) {
          console.error('쿠키 갱신 실패:', refreshError);
        }
      } else {
        console.error('이미 재시도했으나 실패');
      }
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    haveTriedRef.current = false;

    if (imageUrl) {
      void tryLoadImage(imageUrl);
    }

    return () => {
      mountedRef.current = false;
    };
  }, [imageUrl]);

  if (!imageSrc) {
    return <div style={imageStyle} />;
  }

  return (
    <img
      src={imageSrc}
      alt=""
      style={imageStyle}
      onError={async (event) => {
        const imgElement = event.currentTarget;
        if (!haveTriedRef.current) {
          await tryLoadImage(imageUrl);
        } else {
          console.error('재시도 후에도 이미지 요청 실패');
          imgElement.style.display = 'none';
        }
      }}
    />
  );
};
